import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { Button, Tag, Select, InputNumber, Popconfirm } from 'antd';
import TableHeaderDropdownSearch from '@/components/data-table/TableHeaderDropdownSearch';
import TableHeaderSearch from '@/components/data-table/TableHeaderSearch';
import TableHeaderSort from '@/components/data-table/TableHeaderSort';
import TableHeaderTitle from '@/components/data-table/TableHeaderTitle';
import { ROUTES, OPERATION, ROLES, PERMISSIONS } from '@/constants';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import useEnforceAccessControl from '@/hooks/useEnforceAccessControl';
import { MEMBER_ROLES, MEMBER_COLUMNS_DEFAULT_PARAMS } from '../../_shared/constants';

export const MemberColumns = ({
  members = [],
  canManage = false,
  editingId,
  editValues,
  setEditValues,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onRemove,
  projectId,
}) => {
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const { t } = useTranslation(['tables', 'forms']);

  const canViewEmployee = useEnforceAccessControl({
    requiredRoles: [
      ROLES.superAdmin,
      ROLES.admin,
      ROLES.client,
      ROLES.clientOfficer,
      ROLES.hrOfficer,
    ],
    requiredPermissions: [PERMISSIONS.employeeView],
  });

  const roleOptions = useMemo(
    () =>
      Object.values(MEMBER_ROLES).map((val) => ({
        label: t(val, { ns: 'forms' }),
        value: val,
      })),
    [t]
  );

  const sectorOptions = useMemo(() => {
    const seen = new Map();
    members.forEach((m) => {
      const s = m.employee?.employeeSector;
      if (s?._id && !seen.has(s._id)) {
        seen.set(s._id, { label: s.sectorName, value: s._id });
      }
    });
    return [...seen.values()];
  }, [members]);

  const [toggleDropdown, setToggleDropdown] = useState({
    employee: false,
    sector: false,
    role: false,
  });

  const [searchTerm, setSearchTerm] = useState(MEMBER_COLUMNS_DEFAULT_PARAMS);

  useEffect(() => {
    const sector = queryParams?.sector
      ? Array.isArray(queryParams.sector)
        ? queryParams.sector
        : [queryParams.sector]
      : undefined;

    const role = queryParams?.role
      ? Array.isArray(queryParams.role)
        ? queryParams.role
        : [queryParams.role]
      : undefined;

    setSearchTerm({
      employee: queryParams?.employee || '',
      sector,
      role,
    });
  }, [queryParams]);

  const handleClose = (name, value) => {
    setToggleDropdown((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchTerm = (name, value) => {
    setSearchTerm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchReset = (name) => {
    setQueryParams({ [name]: undefined });
    setSearchTerm((prev) => ({ ...prev, [name]: undefined }));
    handleClose(name, false);
  };

  const handleCheckboxReset = (name) => {
    setQueryParams({ [name]: undefined });
    setSearchTerm((prev) => ({ ...prev, [name]: [] }));
    handleClose(name, false);
  };

  const handleSubmit = (name, form) => {
    const value = form[name];
    handleClose(name, false);
    if (value && value?.length > 0) {
      setQueryParams({ [name]: value });
    } else {
      setQueryParams({ [name]: undefined });
    }
  };

  return [
    {
      title: () => (
        <TableHeaderTitle title={t('employee', { ns: 'tables' })}>
          <TableHeaderSearch
            dataIndex="employee"
            title={t('employee', { ns: 'tables' })}
            searchTerm={searchTerm}
            onChange={handleSearchTerm}
            onSubmit={handleSubmit}
            onReset={handleSearchReset}
            toggleDropdown={toggleDropdown}
            onClose={handleClose}
            placement="bottomLeft"
          />
          <TableHeaderSort dataIndex="employee" />
        </TableHeaderTitle>
      ),
      key: 'employee',
      width: '30%',
      render: (_, record) => {
        const emp = record.employee || {};
        const name =
          `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || '-';
        if (canViewEmployee && emp._id) {
          const url = `${ROUTES.admin}/${ROUTES.employees}/${OPERATION.view}/${emp._id}`;
          return (
            <Link
              to={url}
              className="inline-flex items-center gap-1 font-semibold hover:opacity-75"
            >
              {name}
              <LinkOutlined className="text-xs" />
            </Link>
          );
        }
        return name;
      },
    },
    {
      title: () => (
        <TableHeaderTitle title={t('sector', { ns: 'forms' })}>
          <TableHeaderDropdownSearch
            dataIndex="sector"
            title={t('sector', { ns: 'forms' })}
            searchTerm={searchTerm}
            toggleDropdown={toggleDropdown}
            options={sectorOptions}
            onChange={handleSearchTerm}
            onReset={handleCheckboxReset}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
          <TableHeaderSort dataIndex="sector" />
        </TableHeaderTitle>
      ),
      key: 'sector',
      width: '25%',
      render: (_, record) =>
        record.employee?.employeeSector?.sectorName || '-',
    },
    {
      title: () => (
        <TableHeaderTitle title={t('role', { ns: 'tables' })}>
          <TableHeaderDropdownSearch
            dataIndex="role"
            title={t('role', { ns: 'tables' })}
            searchTerm={searchTerm}
            toggleDropdown={toggleDropdown}
            options={roleOptions}
            onChange={handleSearchTerm}
            onReset={handleCheckboxReset}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
          <TableHeaderSort dataIndex="role" />
        </TableHeaderTitle>
      ),
      dataIndex: 'role',
      key: 'role',
      width: '18%',
      render: (role, record) => {
        const empId = record.employee?._id || record.employee;
        if (canManage && editingId === empId) {
          return (
            <Select
              size="small"
              value={editValues.role}
              onChange={(val) =>
                setEditValues((prev) => ({ ...prev, role: val }))
              }
              options={roleOptions}
              className="w-full"
            />
          );
        }
        return (
          <Tag color={role === 'lead' ? 'blue' : 'default'}>
            {t(role, { ns: 'forms' })}
          </Tag>
        );
      },
    },
    ...(canManage
      ? [
          {
            title: () => (
              <TableHeaderTitle title={t('hourlyRate', { ns: 'forms' })}>
                <TableHeaderSort dataIndex="hourlyRate" />
              </TableHeaderTitle>
            ),
            key: 'hourlyRate',
            width: '18%',
            render: (_, record) => {
              const empId = record.employee?._id || record.employee;
              if (editingId === empId) {
                return (
                  <InputNumber
                    size="small"
                    min={0}
                    value={editValues.hourlyRate}
                    onChange={(v) =>
                      setEditValues((prev) => ({ ...prev, hourlyRate: v }))
                    }
                    className="w-full"
                  />
                );
              }
              return record.hourlyRate != null ? record.hourlyRate : '-';
            },
          },
          {
            title: t('actions', { ns: 'tables' }),
            key: 'actions',
            width: '9%',
            render: (_, record) => {
              const empId = record.employee?._id || record.employee;
              if (editingId === empId) {
                return (
                  <div className="flex gap-1">
                    <Button
                      type="text"
                      size="small"
                      icon={<CheckOutlined />}
                      onClick={onSaveEdit}
                    />
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={onCancelEdit}
                    />
                  </div>
                );
              }
              return (
                <div className="flex gap-1">
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => onStartEdit(record)}
                  />
                  <Popconfirm
                    title={t('confirmRemove', { ns: 'forms' })}
                    onConfirm={() =>
                      onRemove(projectId, empId)
                    }
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                    />
                  </Popconfirm>
                </div>
              );
            },
          },
        ]
      : []),
  ];
};
